import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, Star, Check, X, CalendarOff } from "lucide-react";
import { format } from "date-fns";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gss_admin_token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [availability, setAvailability] = useState({ blocked_dates: [], booked: [] });
  const [resendKey, setResendKey] = useState("");
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [keySaving, setKeySaving] = useState(false);

  const authed = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [q, r, a, s] = await Promise.all([
        axios.get(`${API}/admin/quotes`, authed),
        axios.get(`${API}/admin/reviews`, authed),
        axios.get(`${API}/availability`),
        axios.get(`${API}/admin/settings`, authed),
      ]);
      setQuotes(q.data);
      setReviews(r.data);
      setAvailability(a.data);
      setResendKey(s.data.resend_api_key || "");
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Could not load data.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const approveReview = async (id, approved) => {
    await axios.patch(`${API}/admin/reviews/${id}`, { approved }, authed);
    setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, approved } : r)));
    toast.success(approved ? "Review published." : "Review hidden.");
  };

  const deleteReview = async (id) => {
    await axios.delete(`${API}/admin/reviews/${id}`, authed);
    setReviews((rs) => rs.filter((r) => r.id !== id));
    toast.success("Review deleted.");
  };

  const deleteQuote = async (id) => {
    await axios.delete(`${API}/admin/quotes/${id}`, authed);
    setQuotes((qs) => qs.filter((q) => q.id !== id));
    toast.success("Quote request deleted.");
  };

  const toggleBlocked = async (day) => {
    if (!day) return;
    const iso = format(day, "yyyy-MM-dd");
    const isBlocked = availability.blocked_dates.includes(iso);
    try {
      if (isBlocked) {
        await axios.delete(`${API}/admin/blocked-dates/${iso}`, authed);
        setAvailability((a) => ({ ...a, blocked_dates: a.blocked_dates.filter((d) => d !== iso) }));
        toast.success(`${format(day, "MMM d")} is available again.`);
      } else {
        await axios.post(`${API}/admin/blocked-dates`, { date: iso }, authed);
        setAvailability((a) => ({ ...a, blocked_dates: [...a.blocked_dates, iso] }));
        toast.success(`${format(day, "MMM d")} blocked — customers can't pick it.`);
      }
    } catch {
      toast.error("Could not update availability.");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwSaving(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: pwCurrent, new_password: pwNew },
        authed
      );
      toast.success("Password updated. Use it next time you log in.");
      setPwCurrent("");
      setPwNew("");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not change password.");
    } finally {
      setPwSaving(false);
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setKeySaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { resend_api_key: resendKey }, authed);
      toast.success("Email alerts settings saved.");
    } catch {
      toast.error("Could not save settings.");
    } finally {
      setKeySaving(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF8FF] px-4" data-testid="admin-login-page">
        <div className="w-full max-w-sm rounded-3xl border border-[#8E7CC3]/15 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
          <div className="flex justify-center">
            <img src="/logo.png" alt="Glow & Shine Sisters Cleaning Co." className="h-28 w-auto" />
          </div>
          <p className="mt-2 text-center text-xs font-semibold uppercase tracking-widest text-[#6B6776]">Owner Login</p>
          <form onSubmit={login} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <Label htmlFor="a-email">Email</Label>
              <Input id="a-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-xl" data-testid="admin-email-input" />
            </div>
            <div>
              <Label htmlFor="a-pass">Password</Label>
              <Input id="a-pass" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 rounded-xl" data-testid="admin-password-input" />
            </div>
            {error && <p className="text-sm font-medium text-red-500" data-testid="admin-login-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#8E7CC3] px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-transform hover:scale-[1.01] disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Sign In
            </button>
          </form>
          <a href="/" className="mt-6 block text-center text-sm text-[#8E7CC3] hover:underline" data-testid="admin-back-home-link">
            ← Back to website
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F9]" data-testid="admin-dashboard">
      <header className="border-b border-[#8E7CC3]/15 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Glow & Shine Sisters Cleaning Co." className="h-12 w-auto" />
            <span className="rounded-full bg-[#E6E6FA] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#674EA7]">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm font-medium text-[#6B6776] hover:text-[#674EA7]" data-testid="admin-view-site-link">View Site</a>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-1.5 rounded-full border border-[#8E7CC3]/25 px-4 py-2 text-sm font-medium text-[#2C2A35] hover:bg-[#E6E6FA]">
              <LogOut className="h-4 w-4" aria-hidden="true" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <Tabs defaultValue="quotes">
          <TabsList className="rounded-full bg-[#E6E6FA]" data-testid="admin-tabs">
            <TabsTrigger value="quotes" className="rounded-full" data-testid="admin-tab-quotes">
              Quote Requests ({quotes.length})
            </TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-full" data-testid="admin-tab-reviews">
              Reviews ({reviews.filter((r) => !r.approved).length} pending)
            </TabsTrigger>
            <TabsTrigger value="availability" className="rounded-full" data-testid="admin-tab-availability">
              Availability ({availability.blocked_dates.length} blocked)
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-full" data-testid="admin-tab-settings">
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="quotes" className="mt-6">
            <div className="overflow-x-auto rounded-2xl border border-[#8E7CC3]/15 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <Table data-testid="admin-quotes-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Cleaning</TableHead>
                    <TableHead>Preferred Date</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="py-10 text-center text-sm text-[#6B6776]" data-testid="admin-quotes-empty">
                        No quote requests yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {quotes.map((q) => (
                    <TableRow key={q.id} data-testid={`admin-quote-row-${q.id}`}>
                      <TableCell className="whitespace-nowrap text-xs text-[#6B6776]">{fmtDate(q.created_at)}</TableCell>
                      <TableCell className="font-medium">{q.name}</TableCell>
                      <TableCell className="text-sm">
                        <div>{q.phone}</div>
                        <div className="text-xs text-[#6B6776]">{q.email}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {q.property_type}
                        {(q.bedrooms || q.bathrooms) && (
                          <div className="text-xs text-[#6B6776]">
                            {q.bedrooms && `${q.bedrooms} bd`}{q.bedrooms && q.bathrooms && " / "}{q.bathrooms && `${q.bathrooms} ba`}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{q.cleaning_type}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {q.preferred_date || "—"}
                        {q.time_slot && <div className="text-xs text-[#674EA7]">{q.time_slot}</div>}
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate text-xs text-[#6B6776]" title={q.details}>{q.details || "—"}</TableCell>
                      <TableCell>
                        <button onClick={() => deleteQuote(q.id)} aria-label="Delete quote request" data-testid={`admin-quote-delete-${q.id}`} className="text-[#6B6776] transition-colors hover:text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <div className="overflow-x-auto rounded-2xl border border-[#8E7CC3]/15 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <Table data-testid="admin-reviews-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Review</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviews.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-10 text-center text-sm text-[#6B6776]" data-testid="admin-reviews-empty">
                        No reviews yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {reviews.map((r) => (
                    <TableRow key={r.id} data-testid={`admin-review-row-${r.id}`}>
                      <TableCell className="whitespace-nowrap text-xs text-[#6B6776]">{fmtDate(r.created_at)}</TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1 text-sm">
                          {r.rating} <Star className="h-3.5 w-3.5 fill-[#D4AF37] text-[#D4AF37]" aria-hidden="true" />
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[280px] text-sm">{r.text}</TableCell>
                      <TableCell>
                        <span className={`rounded-full px-3 py-1 text-xs font-bold ${r.approved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                          {r.approved ? "Published" : "Pending"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <button
                            onClick={() => approveReview(r.id, !r.approved)}
                            aria-label={r.approved ? "Hide review" : "Approve review"}
                            data-testid={`admin-review-approve-${r.id}`}
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-[#8E7CC3]/25 text-[#674EA7] transition-colors hover:bg-[#E6E6FA]"
                          >
                            {r.approved ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => deleteReview(r.id)}
                            aria-label="Delete review"
                            data-testid={`admin-review-delete-${r.id}`}
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-[#8E7CC3]/25 text-[#6B6776] transition-colors hover:text-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="availability" className="mt-6">
            <div className="grid gap-8 lg:grid-cols-2">
              <div className="rounded-2xl border border-[#8E7CC3]/15 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="admin-availability-calendar">
                <h3 className="flex items-center gap-2 text-lg font-medium text-[#2C2A35]">
                  <CalendarOff className="h-5 w-5 text-[#674EA7]" aria-hidden="true" />
                  Block days off
                </h3>
                <p className="mt-1 text-sm text-[#6B6776]">
                  Click a date to block it — customers won't be able to pick it in the quote form. Click again to unblock.
                </p>
                <div className="mt-4 flex justify-center">
                  <Calendar
                    mode="single"
                    onSelect={toggleBlocked}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    modifiers={{ blocked: availability.blocked_dates.map((d) => new Date(d + "T12:00:00")) }}
                    modifiersClassNames={{ blocked: "bg-red-100 text-red-500 line-through hover:bg-red-100" }}
                    className="rounded-xl border border-[#8E7CC3]/15"
                  />
                </div>
              </div>
              <div className="rounded-2xl border border-[#8E7CC3]/15 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <h3 className="text-lg font-medium text-[#2C2A35]">Blocked dates</h3>
                <ul className="mt-4 space-y-2" data-testid="admin-blocked-list">
                  {availability.blocked_dates.length === 0 && (
                    <li className="text-sm text-[#6B6776]">No blocked dates — every day is bookable.</li>
                  )}
                  {[...availability.blocked_dates].sort().map((d) => (
                    <li key={d} className="flex items-center justify-between rounded-xl bg-[#FAF8FF] px-4 py-2.5 text-sm font-medium text-[#2C2A35]" data-testid={`admin-blocked-${d}`}>
                      {format(new Date(d + "T12:00:00"), "EEEE, MMMM d, yyyy")}
                      <button onClick={() => toggleBlocked(new Date(d + "T12:00:00"))} aria-label={`Unblock ${d}`} data-testid={`admin-unblock-${d}`} className="text-[#6B6776] transition-colors hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
                <h3 className="mt-8 text-lg font-medium text-[#2C2A35]">Booked slots</h3>
                <ul className="mt-4 space-y-2" data-testid="admin-booked-list">
                  {availability.booked.length === 0 && (
                    <li className="text-sm text-[#6B6776]">No slots requested yet.</li>
                  )}
                  {availability.booked.map((b, i) => (
                    <li key={i} className="flex items-center justify-between rounded-xl bg-[#E6E6FA]/60 px-4 py-2.5 text-sm font-medium text-[#2C2A35]">
                      <span>{format(new Date(b.date + "T12:00:00"), "EEE, MMM d")}</span>
                      <span className="text-xs font-bold text-[#674EA7]">{b.slot}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="settings" className="mt-6">
            <div className="grid gap-8 lg:grid-cols-2">
              <div className="rounded-2xl border border-[#8E7CC3]/15 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="admin-password-card">
                <h3 className="text-lg font-medium text-[#2C2A35]">Change password</h3>
                <p className="mt-1 text-sm text-[#6B6776]">This changes your owner login on the live site too.</p>
                <form onSubmit={changePassword} className="mt-5 space-y-4" data-testid="admin-password-form">
                  <div>
                    <Label htmlFor="pw-current">Current password</Label>
                    <Input id="pw-current" type="password" required value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} className="mt-1.5 rounded-xl" data-testid="admin-current-password-input" />
                  </div>
                  <div>
                    <Label htmlFor="pw-new">New password</Label>
                    <Input id="pw-new" type="password" required minLength={6} value={pwNew} onChange={(e) => setPwNew(e.target.value)} className="mt-1.5 rounded-xl" data-testid="admin-new-password-input" />
                  </div>
                  <button type="submit" disabled={pwSaving} data-testid="admin-password-save-button" className="flex items-center gap-2 rounded-full bg-[#8E7CC3] px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-transform hover:scale-[1.01] disabled:opacity-60">
                    {pwSaving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                    Update Password
                  </button>
                </form>
              </div>
              <div className="rounded-2xl border border-[#8E7CC3]/15 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="admin-email-card">
                <h3 className="text-lg font-medium text-[#2C2A35]">Email alerts</h3>
                <p className="mt-1 text-sm text-[#6B6776]">
                  Your Resend API key powers the quote alert and confirmation emails. Paste it here and save.
                </p>
                <form onSubmit={saveSettings} className="mt-5 space-y-4" data-testid="admin-settings-form">
                  <div>
                    <Label htmlFor="resend-key">Resend API key</Label>
                    <Input id="resend-key" type="text" value={resendKey} onChange={(e) => setResendKey(e.target.value)} placeholder="re_..." className="mt-1.5 rounded-xl font-mono text-sm" data-testid="admin-resend-key-input" />
                  </div>
                  <button type="submit" disabled={keySaving} data-testid="admin-settings-save-button" className="flex items-center gap-2 rounded-full bg-[#8E7CC3] px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-transform hover:scale-[1.01] disabled:opacity-60">
                    {keySaving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                    Save Email Settings
                  </button>
                  <p className="text-xs text-[#6B6776]">Alerts go to glowandshinesisterscleaningco@gmail.com</p>
                </form>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
